// src/components/AttendanceReport.jsx
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, TextField, FormControl,
  InputLabel, Select, MenuItem, Button, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Chip, CircularProgress, Tooltip, Avatar, Grid
} from '@mui/material';
import { jwtDecode } from 'jwt-decode';
import SearchIcon from '@mui/icons-material/Search';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

// CSV Export Helper
const exportAttendanceCSV = (data, filename, hideSubContractor) => {
  if (!data || !data.length) {
    alert('No attendance records available to export.');
    return;
  }

  const headers = [
    'Emp Code', 'Employee Name', 'Project Code', 'Division',
    'Punch Status', 'Review Status', 'Category',
    ...(hideSubContractor ? [] : ['Sub Contractor']),
    'Timestamp'
  ];

  const csvRows = [headers.join(',')];

  data.forEach((row) => {
    const values = [
      row.empCode || '',
      `"${(row.name || '').replace(/"/g, '""')}"`,
      row.projectCode || '',
      row.divCode || '',
      row.status || '',
      row.reviewStatus || '',
      row.category || '',
      ...(hideSubContractor ? [] : [`"${(row.subContractor || '').replace(/"/g, '""')}"`]),
      `"${row.timestamp ? new Date(row.timestamp).toLocaleString('en-IN') : ''}"`
    ];
    csvRows.push(values.join(','));
  });

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// List of invalid / test project codes to filter out
const INVALID_PROJECT_CODES = new Set([
  'UNKNOWN', 'WFFWFW', 'ASWW', 'TEST', 'ALL', 'UNDEFINED', 'NULL', ''
]);

const AttendanceReport = () => {
  const token = localStorage.getItem('token');
  const decoded = useMemo(() => {
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }, [token]);

  // Normalized Role Check
  const normalizedRole = useMemo(() => {
    const r = String(decoded?.role || decoded?.userRole || '').trim().toLowerCase();
    if (r === 'poweruser' || r === 'power user' || decoded?.isPowerUser) return 'powerUser';
    if (r === 'admin') return 'admin';
    return 'user';
  }, [decoded]);

  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const initialFilters = {
    empCode: '',
    name: '',
    projectCode: '',
    divCode: '',
    status: '',
    reviewStatus: '',
    category: '',
    subContractor: '',
    dateFrom: '',
    dateTo: ''
  };

  const [filters, setFilters] = useState(initialFilters);
  const [rawFilterOptions, setRawFilterOptions] = useState({
    projectCodes: [],
    divCodes: [],
    statuses: [],
    reviewStatuses: [],
    categories: [],
    subContractors: []
  });

  // Load Filter Dropdown Options
  useEffect(() => {
    axios.get('https://gannondunkerley.in/reports/filters')
      .then(res => setRawFilterOptions(res.data || {}))
      .catch(err => console.error('Error fetching filters:', err));
  }, []);

  // Filter & Clean Project Codes (Removes UNKNOWN, wffwfw, ALL, and duplicates)
  const cleanProjectCodes = useMemo(() => {
    const list = rawFilterOptions.projectCodes || [];
    const uniqueValidCodes = new Set();

    list.forEach(code => {
      const cleanCode = String(code || '').trim().toUpperCase();
      if (cleanCode && !INVALID_PROJECT_CODES.has(cleanCode)) {
        uniqueValidCodes.add(cleanCode);
      }
    });

    return Array.from(uniqueValidCodes).sort();
  }, [rawFilterOptions.projectCodes]);

  // Check if Sub Contractor should be hidden
  const isEmployeeCategory = filters.category?.toLowerCase() === 'employee';

  // Set default restrictions based on role
  useEffect(() => {
    if (normalizedRole === 'user' && decoded?.empCode) {
      setFilters(prev => ({ ...prev, empCode: decoded.empCode }));
    } else if (normalizedRole === 'admin' && decoded?.projectCode) {
      setFilters(prev => ({ ...prev, projectCode: decoded.projectCode }));
    }
  }, [normalizedRole, decoded]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Agar category Employee select ki jaye, toh subContractor filter reset kar dein
    if (name === 'category' && value.toLowerCase() === 'employee') {
      setFilters(prev => ({ ...prev, category: value, subContractor: '' }));
    } else {
      setFilters(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      ...initialFilters,
      empCode: normalizedRole === 'user' ? (decoded?.empCode || '') : '',
      projectCode: normalizedRole === 'admin' ? (decoded?.projectCode || '') : ''
    });
  };

  const handleSearch = () => {
    setLoading(true);
    setPage(0);

    const queryParams = { ...filters };
    if (queryParams.category?.toLowerCase() === 'employee') {
      delete queryParams.subContractor;
    }

    axios.get('https://gannondunkerley.in/reports/attendance', { params: queryParams })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setAttendanceData(data);
      })
      .catch(err => {
        console.error('Error fetching attendance:', err);
        setAttendanceData([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Metrics Summary
  const stats = useMemo(() => {
    const total = attendanceData.length;
    const presentIn = attendanceData.filter(d =>
      ['present', 'in'].includes(String(d.status).trim().toLowerCase())
    ).length;
    const pendingReviews = attendanceData.filter(d =>
      String(d.reviewStatus).trim().toLowerCase() === 'pending'
    ).length;
    return { total, presentIn, pendingReviews };
  }, [attendanceData]);

  // Paginated Rows
  const paginatedData = useMemo(() => {
    return attendanceData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [attendanceData, page, rowsPerPage]);

  return (
    <Box className="content" sx={{ p: { xs: 2, md: 3.5 }, bgcolor: '#f4f6fa', minHeight: '100vh' }}>
      
      {/* Top Header Banner */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-between',
        alignItems: { md: 'center' },
        gap: 2,
        mb: 3
      }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
              Attendance Report
            </Typography>
            <Chip
              label={normalizedRole === 'powerUser' ? 'Executive Access' : normalizedRole === 'admin' ? 'Project Admin' : 'Staff View'}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: '0.72rem',
                bgcolor: '#e0e7ff',
                color: '#4338ca',
                borderRadius: 1.5
              }}
            />
          </Box>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Biometric terminal punch auditing, muster roll tracking & shift verification
          </Typography>
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            onClick={handleResetFilters}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#ffffff',
              borderColor: '#cbd5e1',
              color: '#475569',
              px: 2,
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8' }
            }}
          >
            Reset
          </Button>

          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={() => exportAttendanceCSV(
              attendanceData,
              `GDCL_Attendance_Audit_${new Date().toISOString().split('T')[0]}`,
              isEmployeeCategory
            )}
            disabled={!attendanceData.length}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              color: '#0f766e',
              borderColor: '#0f766e',
              px: 2,
              '&:hover': { bgcolor: '#f0fdfa', borderColor: '#115e59' }
            }}
          >
            Export CSV
          </Button>

          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={loading}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 700,
              bgcolor: '#0284c7',
              px: 2.5,
              boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)',
              '&:hover': { bgcolor: '#0369a1' }
            }}
          >
            {loading ? 'Searching...' : 'Generate Report'}
          </Button>
        </Box>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        {[
          { label: 'Total Logs Retrieved', count: stats.total, icon: <AssessmentIcon />, color: '#0284c7', bg: '#e0f2fe' },
          { label: 'In / Present Punches', count: stats.presentIn, icon: <CheckCircleOutlineIcon />, color: '#16a34a', bg: '#dcfce7' },
          { label: 'Pending Reviews', count: stats.pendingReviews, icon: <PendingActionsIcon />, color: '#ea580c', bg: '#ffedd5' }
        ].map((item, idx) => (
          <Grid item xs={12} sm={4} key={idx}>
            <Paper elevation={0} sx={{
              p: 2.5,
              borderRadius: 3,
              border: '1px solid #e2e8f0',
              bgcolor: '#ffffff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 2px 6px rgba(0,0,0,0.02)'
            }}>
              <Box>
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  {item.label}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', mt: 0.5 }}>
                  {item.count}
                </Typography>
              </Box>
              <Avatar sx={{ bgcolor: item.bg, color: item.color, width: 48, height: 48, borderRadius: 2.5 }}>
                {item.icon}
              </Avatar>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filter Matrix Section */}
      <Paper elevation={0} sx={{
        p: 3,
        mb: 3,
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        bgcolor: '#ffffff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.02)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
          <FilterAltIcon sx={{ color: '#0284c7', fontSize: 20 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0f172a' }}>
            Filter Query Parameters
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          {/* Emp Code */}
          <TextField
            size="small"
            label="Emp Code"
            name="empCode"
            placeholder="e.g. 115061"
            value={filters.empCode}
            onChange={handleChange}
            disabled={normalizedRole === 'user'}
            sx={{
              minWidth: 175,
              flex: '1 1 175px',
              bgcolor: '#ffffff',
              '& .MuiOutlinedInput-root': { borderRadius: 2 }
            }}
          />

          {/* Cleaned Project Code (No UNKNOWN, no wffwfw, sorted) */}
          {normalizedRole === 'admin' ? (
            <TextField
              size="small"
              label="Project Code"
              name="projectCode"
              value={decoded?.projectCode || ''}
              disabled
              sx={{
                minWidth: 185,
                flex: '1 1 185px',
                bgcolor: '#f8fafc',
                '& .MuiOutlinedInput-root': { borderRadius: 2 }
              }}
            />
          ) : (
            <FormControl size="small" sx={{ minWidth: 195, flex: '1 1 195px' }} disabled={normalizedRole === 'user'}>
              <InputLabel id="project-label">Project Code</InputLabel>
              <Select
                labelId="project-label"
                name="projectCode"
                value={filters.projectCode}
                label="Project Code"
                onChange={handleChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Projects</MenuItem>
                {cleanProjectCodes.map(code => (
                  <MenuItem key={code} value={code}>{code}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Division */}
          <FormControl size="small" sx={{ minWidth: 175, flex: '1 1 175px' }} disabled={normalizedRole === 'user'}>
            <InputLabel id="division-label">Division</InputLabel>
            <Select
              labelId="division-label"
              name="divCode"
              value={filters.divCode}
              label="Division"
              onChange={handleChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All Divisions</MenuItem>
              {(rawFilterOptions.divCodes || []).map(code => (
                <MenuItem key={code} value={code}>{code}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status */}
          <FormControl size="small" sx={{ minWidth: 175, flex: '1 1 175px' }}>
            <InputLabel id="status-label">Status</InputLabel>
            <Select
              labelId="status-label"
              name="status"
              value={filters.status}
              label="Status"
              onChange={handleChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All Statuses</MenuItem>
              {(rawFilterOptions.statuses || []).map(st => (
                <MenuItem key={st} value={st}>{st}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Review Status */}
          <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
            <InputLabel id="review-label">Review Status</InputLabel>
            <Select
              labelId="review-label"
              name="reviewStatus"
              value={filters.reviewStatus}
              label="Review Status"
              onChange={handleChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All Reviews</MenuItem>
              {(rawFilterOptions.reviewStatuses || []).map(st => (
                <MenuItem key={st} value={st}>{st}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Category */}
          <FormControl size="small" sx={{ minWidth: 175, flex: '1 1 175px' }} disabled={normalizedRole === 'user'}>
            <InputLabel id="category-label">Category</InputLabel>
            <Select
              labelId="category-label"
              name="category"
              value={filters.category}
              label="Category"
              onChange={handleChange}
              sx={{ borderRadius: 2 }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {(rawFilterOptions.categories || []).map(cat => (
                <MenuItem key={cat} value={cat}>{cat}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Sub Contractor Filter (AUTOMATICALLY HIDDEN IF CATEGORY IS EMPLOYEE) */}
          {!isEmployeeCategory && (
            <FormControl size="small" sx={{ minWidth: 190, flex: '1 1 190px' }} disabled={normalizedRole === 'user'}>
              <InputLabel id="subcontractor-label">Sub Contractor</InputLabel>
              <Select
                labelId="subcontractor-label"
                name="subContractor"
                value={filters.subContractor}
                label="Sub Contractor"
                onChange={handleChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Sub Contractors</MenuItem>
                {(rawFilterOptions.subContractors || []).map(sc => (
                  <MenuItem key={sc} value={sc}>{sc}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Date From */}
          <TextField
            size="small"
            label="From Date"
            name="dateFrom"
            type="date"
            value={filters.dateFrom}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: 165,
              flex: '1 1 165px',
              bgcolor: '#ffffff',
              '& .MuiOutlinedInput-root': { borderRadius: 2 }
            }}
          />

          {/* Date To */}
          <TextField
            size="small"
            label="To Date"
            name="dateTo"
            type="date"
            value={filters.dateTo}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: 165,
              flex: '1 1 165px',
              bgcolor: '#ffffff',
              '& .MuiOutlinedInput-root': { borderRadius: 2 }
            }}
          />
        </Box>
      </Paper>

      {/* Main Table Register */}
      <Paper elevation={0} sx={{
        borderRadius: 3,
        border: '1px solid #e2e8f0',
        bgcolor: '#ffffff',
        overflow: 'hidden',
        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
      }}>
        {loading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
            <CircularProgress size={36} thickness={4} sx={{ color: '#0284c7' }} />
            <Typography variant="body2" sx={{ color: '#64748b', mt: 2, fontWeight: 600 }}>
              Querying attendance registers & compiling logs...
            </Typography>
          </Box>
        )}

        {!loading && (
          <>
            <TableContainer sx={{ maxHeight: 580 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{
                    '& th': {
                      bgcolor: '#f8fafc',
                      fontWeight: 800,
                      color: '#334155',
                      py: 1.5,
                      borderBottom: '2px solid #e2e8f0'
                    }
                  }}>
                    <TableCell>Emp Code</TableCell>
                    <TableCell>Employee Name</TableCell>
                    <TableCell>Project</TableCell>
                    <TableCell>Division</TableCell>
                    <TableCell>Punch Status</TableCell>
                    <TableCell>Review Status</TableCell>
                    <TableCell>Category</TableCell>
                    {/* HIDE SUB CONTRACTOR COLUMN FOR EMPLOYEE */}
                    {!isEmployeeCategory && <TableCell>Sub Contractor</TableCell>}
                    <TableCell align="right">Timestamp</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendanceData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isEmployeeCategory ? 8 : 9} align="center" sx={{ py: 8, color: '#94a3b8' }}>
                        <AssessmentIcon sx={{ fontSize: 44, color: '#cbd5e1', mb: 1 }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#64748b' }}>
                          No records match your criteria
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#94a3b8' }}>
                          Adjust your date range or filters and click "Generate Report"
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row, idx) => {
                      const statusClean = String(row.status || '').trim().toUpperCase();
                      const isPresent = statusClean === 'IN' || statusClean === 'PRESENT';
                      const reviewClean = String(row.reviewStatus || '').trim().toLowerCase();
                      const isConfirmed = reviewClean === 'confirmed' || reviewClean === 'approved';

                      return (
                        <TableRow
                          key={idx}
                          hover
                          sx={{
                            '&:nth-of-type(even)': { bgcolor: '#fafafa' },
                            '&:last-child td': { border: 0 },
                            transition: 'background-color 0.15s ease'
                          }}
                        >
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 700, color: '#0f172a' }}>
                            {row.empCode}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {row.name || 'Staff Member'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.projectCode || 'N/A'}
                              size="small"
                              variant="outlined"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                color: '#1e293b',
                                borderColor: '#cbd5e1'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#475569', fontWeight: 500 }}>
                            {row.divCode}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statusClean || 'LOG'}
                              size="small"
                              sx={{
                                fontWeight: 800,
                                fontSize: '0.7rem',
                                px: 0.5,
                                bgcolor: isPresent ? '#ecfdf5' : '#fef2f2',
                                color: isPresent ? '#059669' : '#dc2626',
                                border: `1px solid ${isPresent ? '#a7f3d0' : '#fecaca'}`
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.reviewStatus || 'None'}
                              size="small"
                              sx={{
                                fontWeight: 700,
                                fontSize: '0.7rem',
                                bgcolor: isConfirmed ? '#eff6ff' : '#fffbeb',
                                color: isConfirmed ? '#1d4ed8' : '#b45309',
                                border: `1px solid ${isConfirmed ? '#bfdbfe' : '#fde68a'}`
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: '#475569' }}>
                            {row.category}
                          </TableCell>

                          {/* ROW CELL: HIDE IF CATEGORY IS EMPLOYEE */}
                          {!isEmployeeCategory && (
                            <TableCell sx={{ color: '#64748b' }}>
                              {row.category?.toLowerCase() === 'employee' ? '—' : (row.subContractor || '—')}
                            </TableCell>
                          )}

                          <TableCell align="right" sx={{ color: '#475569', fontSize: '0.8rem', fontWeight: 500 }}>
                            {row.timestamp ? new Date(row.timestamp).toLocaleString('en-IN') : 'N/A'}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination Controls */}
            <TablePagination
              rowsPerPageOptions={[15, 25, 50, 100]}
              component="div"
              count={attendanceData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              sx={{
                borderTop: '1px solid #e2e8f0',
                bgcolor: '#ffffff',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontWeight: 600,
                  color: '#64748b'
                }
              }}
            />
          </>
        )}
      </Paper>
    </Box>
  );
};

export default AttendanceReport;